import React, { Fragment, ReactNode } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
    Typography,
    Paper,
    Grid,
    IconButton,
    Tooltip,
    Container,
    Button,
    styled,
} from '@mui/material';
import Icon from '@mdi/react';
import { mdiArrowLeft } from '@mdi/js';

import { ReactComponent as ErrorGraphic } from '../assets/images/Network Insights_Error_Graphic.svg';
import { privateRoutes } from '../routes/appRouteList';

const classesPrefix = 'error';

const classes = {
    container: `${classesPrefix}-container`,
    content: `${classesPrefix}-content`,
    paper: `${classesPrefix}-paper`,
    header: `${classesPrefix}-header`,
    headerBtn: `${classesPrefix}-headerBtn`,
    image: `${classesPrefix}-image`,
    imageWrapper: `${classesPrefix}-imageWrapper`,
    btnContainer: `${classesPrefix}-btnContainer`,
    txt: `${classesPrefix}-txt`,
    actionBtn: `${classesPrefix}-actionBtn`,
};

const StyledContainer = styled(Container)(({ theme }) => {
    return {
        [`& .${classes.container}`]: {
            height: '80%',
        },
        [`& .${classes.content}`]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing(3),
        },
        [`& .${classes.paper}`]: {
            height: '100%',
            margin: theme.spacing(2),
            borderRadius: 0,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            overflow: 'auto',
        },
        [`& .${classes.header}`]: {
            backgroundColor: theme.palette.primary.main,
            width: '100%',
            height: theme.spacing(6),
        },
        [`& .${classes.headerBtn}`]: {
            color: theme.palette.getContrastText(theme.palette.primary.main),
        },
        [`& .${classes.image}`]: {
            height: 'auto',
            width: '100%',
        },
        [`& .${classes.imageWrapper}`]: {
            padding: theme.spacing(4),
        },
        [`& .${classes.btnContainer}`]: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            margin: theme.spacing(1),
        },
        [`& .${classes.txt}`]: {
            textAlign: 'center',
        },
        [`& .${classes.actionBtn}`]: {
            margin: theme.spacing(2),
        },
    };
});

const Error = ({
    messages,
    defaultReason,
    hideBackBtn,
    onNavigation,
    children,
}: {
    messages: string[];
    defaultReason: string;
    hideBackBtn?: boolean;
    onNavigation?: () => void;
    children?: ReactNode;
}): React.JSX.Element => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const random = Math.floor(Math.random() * messages.length);

    const message = searchParams.get('message');
    const reason = searchParams.get('reason');

    return (
        <StyledContainer>
            <Paper elevation={2} className={classes.paper}>
                <div className={classes.header}>
                    {!hideBackBtn && (
                        <Tooltip title='Go Back' placement='top'>
                            <IconButton
                                onClick={(): void => {
                                    if (onNavigation) {
                                        onNavigation();
                                    }
                                    navigate(-2);
                                }}
                                className={classes.headerBtn}
                                size='large'
                            >
                                <Icon path={mdiArrowLeft} size={1} />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
                <Grid container className={classes.container}>
                    <Grid item xs md={6} className={classes.content}>
                        <Typography variant='h3' className={classes.txt}>
                            {message || messages[random]}
                        </Typography>
                        <Typography variant='h6' className={classes.txt}>
                            {reason || defaultReason || 'Something went wrong...'}
                        </Typography>
                        {children ? null : (
                            <Typography
                                variant='subtitle1'
                                className={classes.txt}
                            >
                                Try one of these options to get back on track,
                                or report the issue to our team!
                            </Typography>
                        )}
                        <div className={classes.btnContainer}>
                            {children || (
                                <Fragment>
                                    <Button
                                        component={RouterLink}
                                        to={privateRoutes.lanes}
                                        variant='contained'
                                        color='primary'
                                        className={classes.actionBtn}
                                        onClick={(): void => {
                                            if (onNavigation) {
                                                onNavigation();
                                            }
                                        }}
                                    >
                                        Lane Dashboard
                                    </Button>
                                    <Button
                                        component={RouterLink}
                                        to={privateRoutes.shippers}
                                        variant='contained'
                                        color='primary'
                                        className={classes.actionBtn}
                                        onClick={(): void => {
                                            if (onNavigation) {
                                                onNavigation();
                                            }
                                        }}
                                    >
                                        Shipper Dashboard
                                    </Button>
                                    <Button
                                        href='https://earlyengagement.trimble.com/project/feedback/view.html?cap=a665fe75f9ff41eb8bb95c2f89d8780b&f=10ef63ef02c24b53a8baf7544821ee9c'
                                        target='_blank'
                                        variant='contained'
                                        color='primary'
                                        className={classes.actionBtn}
                                    >
                                        Report Issue
                                    </Button>
                                </Fragment>
                            )}
                        </div>
                    </Grid>
                    <Grid item xs md={6} className={classes.imageWrapper}>
                        <ErrorGraphic className={classes.image} />
                    </Grid>
                </Grid>
            </Paper>
        </StyledContainer>
    );
};

export default Error;
